import type { WorkSegment, JobTransition, BreakEntry, JobAllocation } from '../types';
import { minutesBetween, getOverlapMinutes } from './date-utils';

export function allocateJobs(
  segments: readonly WorkSegment[],
  transitions: readonly JobTransition[],
  breaks: readonly BreakEntry[]
): readonly JobAllocation[] {
  if (transitions.length === 0) return [];

  const sortedTransitions = [...transitions].sort((a, b) => a.sequence - b.sequence);
  const sortedSegments = [...segments]
    .filter((s) => s.clockOut !== null)
    .sort((a, b) => a.clockIn.getTime() - b.clockIn.getTime());

  const allocations = new Map<
    string,
    { allocation: Omit<JobAllocation, 'minutes'>; minutes: number }
  >();

  for (const segment of sortedSegments) {
    const segStart = segment.clockIn.getTime();
    const segEnd = segment.clockOut!.getTime();

    // Find transitions that start within this segment
    const relevantTransitions = sortedTransitions.filter(
      (t) => t.startedAt.getTime() >= segStart && t.startedAt.getTime() < segEnd
    );

    if (relevantTransitions.length === 0) continue;

    for (let i = 0; i < relevantTransitions.length; i++) {
      const transition = relevantTransitions[i];
      const jobStart = transition.startedAt;
      const jobEnd =
        i < relevantTransitions.length - 1
          ? relevantTransitions[i + 1].startedAt
          : segment.clockOut!;

      // Calculate minutes for this job span, minus breaks
      let jobMinutes = minutesBetween(jobStart, jobEnd);

      for (const brk of breaks) {
        jobMinutes -= getOverlapMinutes(brk.startTime, brk.endTime, jobStart, jobEnd);
      }

      jobMinutes = Math.max(0, Math.round(jobMinutes));

      const key = `${transition.jobType}-${transition.jobId}-${transition.customerId ?? 'none'}-${transition.classificationCode ?? 'none'}`;
      const existing = allocations.get(key);
      if (existing) {
        allocations.set(key, { ...existing, minutes: existing.minutes + jobMinutes });
      } else {
        allocations.set(key, {
          allocation: {
            jobType: transition.jobType,
            jobId: transition.jobId,
            customerId: transition.customerId,
            isPrevailingWage: false, // Set by caller based on customer PW flag
            classificationCode: transition.classificationCode,
          },
          minutes: jobMinutes,
        });
      }
    }
  }

  return Array.from(allocations.values()).map(({ allocation, minutes }) => ({
    ...allocation,
    minutes,
  }));
}
